import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { NavigationGroup, NAVIGATION_GROUP_LABELS, NAVIGATION_GROUP_ORDER } from '@/constants/residents/residentNavigation';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { AnimatedAccordion } from '@/components/common/AnimatedAccordion';

export type DesktopSidebarItem = {
  key: string;
  title: string;
  icon?: React.ReactNode;
  onPress: () => void;
  isActive?: boolean;
  group?: NavigationGroup;
};

type DesktopSidebarProps = {
  items: DesktopSidebarItem[];
  footer?: React.ReactNode;
};

export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({ items, footer }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<NavigationGroup>>(() => new Set(['QUICK_ACTIONS']));

  const hasGroups = items.some((item) => item.group);

  const groupedItems = hasGroups
    ? NAVIGATION_GROUP_ORDER.reduce((acc, group) => {
        acc[group] = items.filter((item) => item.group === group);
        return acc;
      }, {} as Record<NavigationGroup, DesktopSidebarItem[]>)
    : null;

  const toggleGroup = useCallback((group: NavigationGroup) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  const renderItem = (item: DesktopSidebarItem) => (
    <TouchableOpacity
      key={item.key}
      style={[styles.item, item.isActive ? styles.itemActive : null]}
      onPress={item.onPress}
    >
      <View style={styles.itemIcon}>{item.icon}</View>
      <Text style={[styles.itemText, item.isActive ? styles.itemTextActive : null]} numberOfLines={1}>
        {item.title}
      </Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.list}>
          {hasGroups && groupedItems
            ? NAVIGATION_GROUP_ORDER.map((group) => {
                const sectionItems = groupedItems[group];
                if (!sectionItems || sectionItems.length === 0) return null;
                const isExpanded = expandedGroups.has(group);

                return (
                  <AnimatedAccordion
                    key={group}
                    isExpanded={isExpanded}
                    onToggle={() => toggleGroup(group)}
                    containerStyle={styles.section}
                    headerStyle={styles.sectionHeaderButton}
                    contentStyle={styles.sectionItems}
                    header={
                      <Text style={[styles.sectionHeader, isExpanded && styles.sectionHeaderActive]}>
                        {NAVIGATION_GROUP_LABELS[group]}
                      </Text>
                    }
                    chevron={
                      <RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN
                        width={16}
                        height={16}
                        color={isExpanded ? RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY : RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY}
                      />
                    }
                  >
                    {sectionItems.map(renderItem)}
                  </AnimatedAccordion>
                );
              })
            : items.map(renderItem)}
        </View>
      </ScrollView>
      {footer ? <View style={styles.footer}>{footer}</View> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 252,
    paddingVertical: 4,
    paddingHorizontal: 16,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  list: {
    flexGrow: 1,
    gap: 8,
  },
  section: {
    width: '100%',
  },
  sectionHeaderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  sectionHeader: {
    fontWeight: '600',
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  sectionHeaderActive: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  sectionItems: {
    width: '100%',
  },
  item: {
    minHeight: 46,
    borderRadius: 16,
    marginVertical: 4,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
  },
  itemActive: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    borderWidth: 2,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_PRIMARY,
  },
  itemIcon: {
    width: 32,
    alignItems: 'center',
    marginRight: 12,
  },
  itemText: {
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
    fontSize: 14,
    flexShrink: 1,
  },
  itemTextActive: {
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
    fontWeight: '600',
  },
  footer: {
    marginTop: 12,
  },
});
